import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, CSSProperties } from 'react';

import { baseClasses, variantClasses, type ButtonVariant } from './button-styles';

/**
 * Props for the Button component
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Full width */
  fullWidth?: boolean;
  /** Icon to display on the left side */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right side */
  rightIcon?: React.ReactNode;
}

// Size styles using CSS variables with fallbacks
const sizeStyles: Record<string, CSSProperties> = {
  xs: {
    paddingTop: 'var(--uiv2-btn-padding-xs-y, 4px)',
    paddingBottom: 'var(--uiv2-btn-padding-xs-y, 4px)',
    paddingLeft: 'var(--uiv2-btn-padding-xs-x, 8px)',
    paddingRight: 'var(--uiv2-btn-padding-xs-x, 8px)',
    fontSize: 'var(--uiv2-btn-font-xs, 14px)',
    lineHeight: '20px',
  },
  sm: {
    paddingTop: 'var(--uiv2-btn-padding-sm-y, 8px)',
    paddingBottom: 'var(--uiv2-btn-padding-sm-y, 8px)',
    paddingLeft: 'var(--uiv2-btn-padding-sm-x, 12px)',
    paddingRight: 'var(--uiv2-btn-padding-sm-x, 12px)',
    fontSize: 'var(--uiv2-btn-font-sm, 14px)',
    lineHeight: '20px',
  },
  md: {
    paddingTop: 'var(--uiv2-btn-padding-md-y, 10px)',
    paddingBottom: 'var(--uiv2-btn-padding-md-y, 10px)',
    paddingLeft: 'var(--uiv2-btn-padding-md-x, 14px)',
    paddingRight: 'var(--uiv2-btn-padding-md-x, 14px)',
    fontSize: 'var(--uiv2-btn-font-md, 16px)',
    lineHeight: '24px',
  },
  lg: {
    paddingTop: 'var(--uiv2-btn-padding-lg-y, 10px)',
    paddingBottom: 'var(--uiv2-btn-padding-lg-y, 10px)',
    paddingLeft: 'var(--uiv2-btn-padding-lg-x, 16px)',
    paddingRight: 'var(--uiv2-btn-padding-lg-x, 16px)',
    fontSize: 'var(--uiv2-btn-font-lg, 16px)',
    lineHeight: '24px',
  },
  xl: {
    paddingTop: 'var(--uiv2-btn-padding-xl-y, 12px)',
    paddingBottom: 'var(--uiv2-btn-padding-xl-y, 12px)',
    paddingLeft: 'var(--uiv2-btn-padding-xl-x, 18px)',
    paddingRight: 'var(--uiv2-btn-padding-xl-x, 18px)',
    fontSize: 'var(--uiv2-btn-font-xl, 16px)',
    lineHeight: '24px',
  },
  '2xl': {
    paddingTop: 'var(--uiv2-btn-padding-2xl-y, 16px)',
    paddingBottom: 'var(--uiv2-btn-padding-2xl-y, 16px)',
    paddingLeft: 'var(--uiv2-btn-padding-2xl-x, 22px)',
    paddingRight: 'var(--uiv2-btn-padding-2xl-x, 22px)',
    fontSize: 'var(--uiv2-btn-font-2xl, 18px)',
    lineHeight: '28px',
  },
};

/**
 * Button component with multiple variants and sizes
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Click me
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      disabled,
      className = '',
      style,
      children,
      leftIcon,
      rightIcon,
      ...props
    },
    ref
  ) => {
    const widthClasses = fullWidth ? 'w-full' : '';

    return (
      <button
        ref={ref}
        disabled={disabled}
        className={`${baseClasses} gap-1 ${variantClasses[variant]} ${widthClasses} ${className}`}
        style={{ ...sizeStyles[size], ...style }}
        {...props}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
